import React from 'react';
import { ArrowLeft, Shield, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTrackLegalPageView } from '../hooks/useTrackLegalPageView';

const PrivacyPolicy = () => {
  const { isDarkMode } = useTheme();
  const currentDate = "August 2026";
  
  // Track page view
  useTrackLegalPageView('privacy-policy');
  
  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDarkMode ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-sm`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 text-sm font-medium ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Title */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
            <Shield className={`w-8 h-8 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
          </div>
          <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Privacy Policy
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Last updated: {currentDate}
          </p>
        </div>

        {/* Policy Content */}
        <div className={`prose prose-lg max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
          
          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>1. Introduction</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              NEURA AI SOFTWARES ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We are based in Belfast, Northern Ireland, and we comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>2. Information We Collect</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We may collect the following types of information:
            </p>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Personal Information</h3>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Name and contact details (email address, phone number)</li>
              <li>Business name and industry</li>
              <li>Billing and payment information</li>
              <li>Account login credentials</li>
              <li>Communications you send to us</li>
            </ul>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Automatically Collected Information</h3>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>IP address and browser type</li>
              <li>Device information</li>
              <li>Pages visited and time spent on our website</li>
              <li>Referring website addresses</li>
              <li>Cookie data (see our Cookie Policy)</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>3. How We Use Your Information</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We use the information we collect to:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Respond to your enquiries and provide customer support</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Analyse website usage to improve user experience</li>
              <li>Comply with legal obligations</li>
              <li>Protect against fraudulent or illegal activity</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>4. Legal Basis for Processing</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Under UK GDPR, we process your personal data based on:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>Contractual necessity:</strong> To fulfil our obligations under a contract with you</li>
              <li><strong>Legitimate interests:</strong> To operate and improve our business</li>
              <li><strong>Consent:</strong> Where you have given explicit consent for specific processing</li>
              <li><strong>Legal obligation:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>5. Data Sharing and Disclosure</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We may share your information with:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>Service providers:</strong> Third parties who perform services on our behalf (hosting, payment processing, analytics)</li>
              <li><strong>Professional advisers:</strong> Lawyers, accountants, and auditors where necessary</li>
              <li><strong>Legal authorities:</strong> When required by law or to protect our rights</li>
              <li><strong>Business transfers:</strong> In connection with any merger, sale, or acquisition</li>
            </ul>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>6. Data Retention</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, including:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Active client data: Duration of our business relationship plus 6 years</li>
              <li>Marketing contacts: Until you unsubscribe or request deletion</li>
              <li>Website analytics: 26 months</li>
              <li>Legal and compliance records: As required by applicable law</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>7. Your Rights</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Under UK GDPR, you have the following rights:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>Right of access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Right to restrict processing:</strong> Request limitation of how we use your data</li>
              <li><strong>Right to data portability:</strong> Receive your data in a portable format</li>
              <li><strong>Right to object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to withdraw consent:</strong> Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              To exercise any of these rights, please use our <Link to="/data-request" className="text-cyan-500 hover:underline">Data Request Form</Link> or contact us using the details below.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>8. Data Security</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Staff training on data protection</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>9. International Transfers</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Some of our service providers may be located outside the UK. Where we transfer personal data internationally, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the UK Information Commissioner's Office (ICO).
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>10. Children's Privacy</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Our services are not intended for individuals under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it promptly.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>11. Changes to This Policy</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>12. Contact Us</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>NEURA AI SOFTWARES</p>
              <div className="space-y-3">
                <div className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <MapPin className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                  <span>Belfast, Northern Ireland, United Kingdom</span>
                </div>
                <div className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <Mail className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                  <a href="mailto:PeterG@neura-ai.info" className="hover:text-cyan-500 transition-colors">
                    PeterG@neura-ai.info
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>13. Complaints</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              If you are not satisfied with how we handle your personal data, you have the right to lodge a complaint with the UK Information Commissioner's Office (ICO):
            </p>
            <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Information Commissioner's Office</p>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">ico.org.uk</a></p>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Phone: 0303 123 1113</p>
            </div>
          </section>

        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-colors ${isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className={`border-t py-6 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            © {new Date().getFullYear()} NEURA AI SOFTWARES. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
